import * as React from "react";
import {
  cancelUploadJob,
  fetchUploadJobItems,
  fetchUploadJobs,
  retryUploadJob,
  streamUploadJobs,
} from "../api/sharepointApi";
import type { UploadJobItemSummary, UploadJobSummary } from "../types/sharepoint";

const ACTIVE_STATUSES = new Set(["pending", "running"]);

export interface UploadJobsState {
  jobs: UploadJobSummary[];
  itemsByJobId: Record<string, UploadJobItemSummary[]>;
  expandedJobIds: Record<string, boolean>;
  loading: boolean;
  error: string;
  refreshJobs: () => Promise<void>;
  toggleJob: (jobId: string) => Promise<void>;
  retryJob: (jobId: string) => Promise<void>;
  cancelJob: (jobId: string) => Promise<void>;
}

function hasActiveJobs(jobs: UploadJobSummary[]): boolean {
  return jobs.some((job) => ACTIVE_STATUSES.has(job.status));
}

function sortActiveJobsFirst(jobs: UploadJobSummary[]): UploadJobSummary[] {
  return [...jobs].sort((a, b) => {
    const activeA = ACTIVE_STATUSES.has(a.status);
    const activeB = ACTIVE_STATUSES.has(b.status);
    if (activeA !== activeB) return activeA ? -1 : 1;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
}

function getVisibleJobs(jobs: UploadJobSummary[]): UploadJobSummary[] {
  const sortedJobs = sortActiveJobsFirst(jobs);
  return hasActiveJobs(sortedJobs) ? sortedJobs : sortedJobs.slice(0, 2);
}

function getFirstActiveJobId(jobs: UploadJobSummary[]): string | undefined {
  return jobs.find((job) => ACTIVE_STATUSES.has(job.status))?.jobId;
}

export function useUploadJobs(refreshKey?: string): UploadJobsState {
  const [jobs, setJobs] = React.useState<UploadJobSummary[]>([]);
  const [itemsByJobId, setItemsByJobId] = React.useState<Record<string, UploadJobItemSummary[]>>({});
  const [expandedJobIds, setExpandedJobIds] = React.useState<Record<string, boolean>>({});
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [streamEnabled, setStreamEnabled] = React.useState(false);

  const applyJobSnapshot = React.useCallback((snapshotJobs: UploadJobSummary[]) => {
    const visibleJobs = getVisibleJobs(snapshotJobs);
    const firstActiveJobId = getFirstActiveJobId(visibleJobs);

    setJobs(visibleJobs);
    if (firstActiveJobId) {
      setExpandedJobIds((prev) => (
        prev[firstActiveJobId] ? prev : { ...prev, [firstActiveJobId]: true }
      ));
    }

    return hasActiveJobs(visibleJobs);
  }, []);

  const refreshJobs = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const hasActive = applyJobSnapshot(await fetchUploadJobs());
      if (hasActive) setStreamEnabled(true);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [applyJobSnapshot]);

  const loadJobItems = React.useCallback(async (jobId: string) => {
    setError("");
    try {
      const items = await fetchUploadJobItems(jobId);
      setItemsByJobId((prev) => ({ ...prev, [jobId]: items }));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  const toggleJob = React.useCallback(
    async (jobId: string) => {
      const willExpand = !expandedJobIds[jobId];
      setExpandedJobIds((prev) => ({ ...prev, [jobId]: willExpand }));
      if (willExpand) {
        await loadJobItems(jobId);
      }
    },
    [expandedJobIds, loadJobItems]
  );

  const retryJob = React.useCallback(
    async (jobId: string) => {
      setError("");
      try {
        await retryUploadJob(jobId);
        setStreamEnabled(true);
        await refreshJobs();
        if (expandedJobIds[jobId]) await loadJobItems(jobId);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [expandedJobIds, loadJobItems, refreshJobs]
  );

  const cancelJob = React.useCallback(
    async (jobId: string) => {
      setError("");
      try {
        await cancelUploadJob(jobId);
        await refreshJobs();
        if (expandedJobIds[jobId]) await loadJobItems(jobId);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [expandedJobIds, loadJobItems, refreshJobs]
  );

  React.useEffect(() => {
    refreshJobs();
    if (refreshKey) setStreamEnabled(true);
  }, [refreshJobs, refreshKey]);

  React.useEffect(() => {
    if (!streamEnabled) return undefined;

    let cleanup: (() => void) | undefined;
    let disposed = false;

    streamUploadJobs(
      (snapshot) => {
        if (disposed) return;
        const hasActive = applyJobSnapshot(snapshot.jobs);

        setItemsByJobId((prev) => ({ ...prev, ...snapshot.itemsByJobId }));
        if (!hasActive) setStreamEnabled(false);
      },
      (message) => {
        if (!disposed) setError(message);
      }
    )
      .then((stop) => {
        if (disposed) {
          stop();
          return;
        }
        cleanup = stop;
      })
      .catch((e: unknown) => {
        if (!disposed) setError(e instanceof Error ? e.message : String(e));
      });

    return () => {
      disposed = true;
      cleanup?.();
    };
  }, [applyJobSnapshot, streamEnabled]);

  return {
    jobs,
    itemsByJobId,
    expandedJobIds,
    loading,
    error,
    refreshJobs,
    toggleJob,
    retryJob,
    cancelJob,
  };
}
